import _ from 'lodash';
import moment, { invalid } from 'moment';
import { createSelector } from 'reselect';

// import CadminCompaniesAx    from 'app/actions/company-admin/companies';
import helpers              from 'app/helpers/rubrics';
// import history              from 'app/history';
// import paths                from 'app/paths';
import reducerUtils         from 'app/reducers/utils';
// import CadminSlx            from 'app/selectors/company-admin/';
// import EntitiesSlx          from 'app/selectors/entities';
// import RoutingSlx           from 'app/selectors/routing';

const {ScalePresetTypes, OptionStyles, GradLabelPresetTypes, NormTypes} = helpers;

// returns an array of weights that are all equal with the given number of items in the array
// returned weights will total 100%
// complexity needed to handle rounding/decimals - we round to the nearest 3 decimal places - without the complexity you could leave 0.001% or so unaccounted for
const getEqualWeights = (count) => {
  if (count < 1) return [];
  const weights = [];
  let left = count;
  let budget = 100_000;
  _.times(count, () => {
    const weight = Math.round(budget / left);
    left -= 1;
    budget -= weight;
    weights.push(weight);
  });
  return weights;
};

// takes an array of weights, and returns a new array of weights which adds up to 100%
// maintains the original array's proportions
const rescaleWeights = (dirtyWeights) => {
  if (!dirtyWeights.length) return [];
  // special case - the given weights are all 0 - just change them to another number
  const isAllZeros = _.sum(dirtyWeights) === 0;
  if (isAllZeros) return rescaleWeights(dirtyWeights.map(w => 1));

  const remainingWeights = [...dirtyWeights];
  let budget = 100_000;
  const newWeights = [];
  while (remainingWeights.length) {
    const total = _.sum(remainingWeights);
    const dirtyWeight = remainingWeights.pop();
    const percentage = total ? (dirtyWeight / total) : 0;
    const newWeight = Math.round(budget * percentage);
    newWeights.unshift(newWeight);
    budget -= newWeight;
  }
  return newWeights;
};



/*
 *  Actions
 */

const Types = {
  SET_RUB:             'CA_RUB_BLDR_SET_RUB',
  SET_CRIT:            'CA_RUB_BLDR_SET_CRIT',
  SET_OPT:             'CA_RUB_BLDR_SET_OPT',
  SET_GRAD_LBL:        'CA_RUB_BLDR_SET_GRAD_LBL',
  UPDATE_RUB:          'CA_RUB_BLDR_UPDATE_RUB',
  UPDATE_CRIT:         'CA_RUB_BLDR_UPDATE_CRIT',
  UPDATE_OPT:          'CA_RUB_BLDR_UPDATE_OPT',
  UPDATE_GRAD_LBL:     'CA_RUB_BLDR_UPDATE_GRAD_LBL',
  UNLOAD_OBJS:         'CA_RUB_BLDR_UNLOAD_OBJS',
};

const Ax = {

  setRub: (rubric) => (dispatch, getState) => {
    (rubric.crits || []).forEach((crit) => {
      dispatch(Ax.setCrit(rubric.id, crit));
    });
    (rubric.gradLbls || []).forEach((gradLbl) => {
      dispatch(Ax.setGradLbl(rubric.id, gradLbl));
    });
    dispatch({type: Types.SET_RUB, rubric});
    dispatch(Ax.checkValidations(rubric.id));
  },
  setCrit: (rubId, crit) => (dispatch, getState) => {
    (crit.opts || []).forEach((opt) => {
      dispatch(Ax.setOpt(crit.id, opt));
    });
    return dispatch({type: Types.SET_CRIT, crit, rubId});
  },
  setOpt: (critId, opt) => {
    return {type: Types.SET_OPT, opt, critId};
  },
  setGradLbl: (rubId, gradLbl) => {
    return {type: Types.SET_GRAD_LBL, rubId, gradLbl};
  },

  updateRub: (rubId, attrs) => {
    return {type: Types.UPDATE_RUB, rubId, attrs};
  },
  updateCrit: (critId, attrs, {skipHasTouched=false}={}) => (dispatch, getState) => {
    const crit = Slx.crit(getState(), critId);
    dispatch({type: Types.UPDATE_CRIT, critId, attrs});
    if (Object.hasOwn(attrs, 'weight') && !skipHasTouched) {
      dispatch(Ax.updateRub(crit.rubId, {hasTouchedWeights: true}));
    }
  },
  updateOpt: (optId, attrs) => {
    return {type: Types.UPDATE_OPT, optId, attrs};
  },
  updateGradLbl: (gradLblId, dirtyAttrs) => (dispatch, getState) => {
    const attrs = {...dirtyAttrs};
    const gradLbl = Slx.gradLblMap(getState())[gradLblId];
    if (gradLbl.threshold === 0) attrs.threshold = 0;
    return dispatch({type: Types.UPDATE_GRAD_LBL, gradLblId, attrs});
  },

  checkValidations: (rubId) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    if (rub.validations) {
      const newAttrs = {showValidations: true};
      const hasInvalidGradLbl = !!((rub.gradLbls || []).find(gl => gl.validations) || rub.validations?.gradLblThresholds);
      if (hasInvalidGradLbl) Object.assign(newAttrs, {editGradLbls: true, showGradLblValidations: true});
      const invalidCrit = (rub.crits || []).find(c => c.validations);
      if (invalidCrit) newAttrs.editCritId = invalidCrit.id;
      dispatch(Ax.updateRub(rubId, newAttrs));
    } else if (rub.showValidations) {
      dispatch(Ax.updateRub(rubId, {showValidations: false}));
    }
  },

  editCrit: (rubId, critId) => (dispatch, getState) => {
    const state = getState();
    const rub = Slx.rub(state, rubId);
    const editCrit = Slx.crit(state, rub.editCritId);
    // if the currently editting crit has validations, show them instead of changing the edit target
    if (editCrit?.validations) {
      dispatch(Ax.updateCrit(editCrit.id, {showValidations: true}));
    } else {
      if (editCrit) dispatch(Ax.sortOpts(editCrit.id));
      dispatch(Ax.updateRub(rubId, {editCritId: critId}));
      if (critId) dispatch(Ax.updateCrit(critId, {showValidations: false}));
    }
    dispatch(Ax.checkValidations(rubId));
  },

  equalizeWeights: (rubId) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    const count = rub.critIds.length;
    if (!count) return;
    const weights = getEqualWeights(count);
    rub.critIds.forEach((critId, index) => {
      dispatch(Ax.updateCrit(critId, {weight: weights[index]}, {skipHasTouched: true}));
    });
  },
  rescaleWeights: (rubId) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    // if (!rub.keepEqual) return;
    const count = rub.critIds.length;
    if (!count) return;
    const weights = rub.crits.map(c => c.weight);
    const fixedWeights = rescaleWeights(weights);
    rub.critIds.forEach((critId, index) => {
      dispatch(Ax.updateCrit(critId, {weight: fixedWeights[index]}));
    });
  },

  addCrit: (rubId) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    const newCrit = helpers.makeCrit(rub.crits || []);
    const newAttrs = {critIds: [...rub.critIds, newCrit.id]};
    dispatch(Ax.setCrit(rubId, newCrit));
    dispatch(Ax.updateRub(rubId, newAttrs));
    if (!rub.hasTouchedWeights) {
      dispatch(Ax.equalizeWeights(rub.id));
    }
  },
  duplicateCrit: (rubId, critId) => (dispatch, getState) => {
    const state = getState();
    const rub = Slx.rub(state, rubId);
    const crit = Slx.crit(state, critId);
    const newCrit = helpers.cloneCrit(crit);
    const newAttrs = {critIds: [...rub.critIds, newCrit.id]};
    dispatch(Ax.setCrit(rubId, newCrit));
    dispatch(Ax.updateRub(rubId, newAttrs));
  },
  deleteCrit: (rubId, critId) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    const oneLeft = rub.crits.length === 2;
    const critIdSet = new Set(rub.critIds || []);
    critIdSet.delete(critId);
    dispatch(Ax.updateRub(rubId, {critIds: [...critIdSet]}));
    dispatch(Ax.unloadCrit(critId));
    if (oneLeft) {
      dispatch(Ax.equalizeWeights(rubId));
      dispatch(Ax.setNormType(rubId, NormTypes.PERCENTAGE));
    }
  },

  addOpt: (critId) => (dispatch, getState) => {
    const crit = Slx.crit(getState(), critId);
    const newOpt = helpers.makeOpt(crit.opts || []);
    const newAttrs = {optIds: [...crit.optIds, newOpt.id], presetKey: ScalePresetTypes.OPTS_CUSTOM};
    dispatch(Ax.setOpt(critId, newOpt));
    dispatch(Ax.updateCrit(critId, newAttrs));
  },
  deleteOpt: (critId, optId) => (dispatch, getState) => {
    const crit = Slx.critMap(getState())[critId];
    const optIdSet = new Set(crit.optIds || []);
    optIdSet.delete(optId);
    dispatch(Ax.updateCrit(critId, {optIds: [...optIdSet], presetKey: ScalePresetTypes.OPTS_CUSTOM}));
    dispatch(Ax.unloadOpt(optId));
  },
  sortOpts: (critId) => (dispatch, getState) => {
    const crit = Slx.crit(getState(), critId);
    let opts = crit.opts ? [...crit.opts] : null;
    if (!opts) return;
    opts = _.sortBy(opts, ['value']);
    const optIds = opts.map(o => o.id);
    dispatch(Ax.updateCrit(critId, {optIds}));
  },

  unloadRub: (rubId) => (dispatch, getState) => {
    const state = getState();
    const rub = Slx.rubMap(state)[rubId];
    if (!rub) return;
    const rubIds = [rub.id];
    const critIds = [];
    const optIds = [];
    const critMap = Slx.critMap(state);
    (rub.critIds || []).forEach((critId) => {
      critIds.push(critId);
      const crit = critMap[critId];
      optIds.push(...(crit?.optIds || []));
    });
    return dispatch(Ax.unloadObjs({rubIds, critIds, optIds}));
  },
  unloadCrit: (critId) => (dispatch, getState) => {
    const crit = Slx.critMap(getState())[critId];
    if (!crit) return;
    const critIds = [crit.id];
    const optIds = crit.optIds || [];
    return dispatch(Ax.unloadObjs({critIds, optIds}));
  },
  unloadOpt: (optId) => (dispatch, getState) => {
    return dispatch(Ax.unloadObjs({optIds: [optId]}));
  },
  unloadGradLbl: (gradLblId) => (dispatch, getState) => {
    return dispatch(Ax.unloadObjs({gradLblIds: [gradLblId]}));
  },
  unloadObjs: ({rubIds=[], critIds=[], optIds=[], gradLblIds=[]} = {}) => {
    return {type: Types.UNLOAD_OBJS, rubIds, critIds, optIds, gradLblIds};
  },

  adoptScalePreset: (critId, presetKey) => (dispatch, getState) => {
    const crit = Slx.critMap(getState())[critId];
    // if (crit.presetKey === presetKey) return;
    const preset = helpers.scalePresets[presetKey];
    let unloadPrevOpts = false;
    // build new crit attrs and store new opt objs if needed
    const newAttrs = {presetKey};
    if (preset) {
      unloadPrevOpts = true;
      if (preset.opts) {
        const opts = preset.opts.map(o => helpers.makeOpt([], o));
        opts.forEach(opt => dispatch(Ax.setOpt(critId, opt)));
        newAttrs.optIds = opts.map(o => o.id);
        newAttrs.range = null;
      } else if (preset.range) {
        newAttrs.optIds = null;
        newAttrs.range = {...preset.range};
      } else {
        throw new Error('each preset must contain a range or opts');
      }
    } else if (presetKey === ScalePresetTypes.OPTS_CUSTOM) {
      newAttrs.range = null;
      if (!crit.optIds) newAttrs.optIds = [];
    } else if (presetKey === ScalePresetTypes.RNG_CUSTOM) {
      unloadPrevOpts = true;
      newAttrs.optIds = null;
      if (!crit.range) newAttrs.range = {min: 0, max: 100};
    }
    //
    dispatch(Ax.updateCrit(critId, newAttrs));
    if (unloadPrevOpts) dispatch(Ax.unloadObjs({optIds: (crit.optIds || [])}));
  },

  setNormType: (rubId, normType) => (dispatch, getState) => {
    const rub = Slx.rubMap(getState())[rubId];
    const isLabels = normType === NormTypes.GRAD_LABELS;
    const newAttrs = {normType, gradLblIds: null};
    dispatch(Ax.updateRub(rubId, newAttrs));
    dispatch(Ax.unloadObjs({gradLblIds: (rub.gradLblIds || [])}));
    if (isLabels) {
      dispatch(Ax.adoptGradLabelPreset(rubId, GradLabelPresetTypes.LETTER_GRADE));
    }
  },

  adoptGradLabelPreset: (rubId, presetKey) => (dispatch, getState) => {
    const rub = Slx.rubMap(getState())[rubId];
    const preset = helpers.gradLabelPresets[presetKey];
    const newAttrs = {gradLabelPresetKey: presetKey};
    let unloadPrev = false;
    if (preset) {
      unloadPrev = true;
      const gradLbls = preset.gradLbls.map(gl => helpers.makeGradLabel([], gl));
      gradLbls.forEach(gradLbl => dispatch(Ax.setGradLbl(rubId, gradLbl)));
      newAttrs.gradLblIds = gradLbls.map(gl => gl.id);
    }
    dispatch(Ax.updateRub(rubId, newAttrs));
    if (unloadPrev) dispatch(Ax.unloadObjs({gradLblIds: (rub.gradLblIds || [])}));
    if (presetKey === GradLabelPresetTypes.CUSTOM) {
      dispatch(Ax.setEditGradLbls(rubId, true));
    }
  },

  addGradLbl: (rubId) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    const gradLbl = helpers.makeGradLabel(rub.gradLbls || []);
    const gradLblIds = [...rub.gradLblIds];
    gradLblIds.splice(gradLblIds.length - 1, 0, gradLbl.id);
    const newAttrs = {gradLblIds, gradLabelPresetKey: GradLabelPresetTypes.CUSTOM};
    dispatch(Ax.setGradLbl(rubId, gradLbl));
    dispatch(Ax.updateRub(rubId, newAttrs));
  },

  deleteGradLbl: (rubId, gradLblId) => (dispatch, getState) => {
    const state = getState();
    const rub = Slx.rubMap(state)[rubId];
    const gradLbl = Slx.gradLblMap(state)[gradLblId];
    if (gradLbl.threshold === 0) return;
    const gradLblIdSet = new Set(rub.gradLblIds || []);
    gradLblIdSet.delete(gradLblId);
    dispatch(Ax.updateRub(rubId, {gradLblIds: [...gradLblIdSet], presetKey: GradLabelPresetTypes.CUSTOM}));
    dispatch(Ax.unloadGradLbl(gradLblId));
  },

  sortGradLbls: (rubId) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    if (!rub?.gradLbls?.length) return;
    const gradLbls = _.orderBy([...rub.gradLbls], 'threshold', 'desc');
    const gradLblIds = gradLbls.map(gl => gl.id);
    dispatch(Ax.updateRub(rubId, {gradLblIds}));;
  },

  setEditGradLbls: (rubId, editGradLbls) => (dispatch, getState) => {
    const rub = Slx.rub(getState(), rubId);
    if (rub.editGradLbls === editGradLbls) return;

    if (editGradLbls) {
      dispatch(Ax.updateRub(rubId, {editGradLbls, showGradLblValidations: false}));
    } else {
      const hasInvalidGradLbl = !!(rub.gradLbls || []).find(gl => gl.validations);
      const hasUniqueValidation = !!rub.validations?.gradLblThresholds;
      if (hasInvalidGradLbl || hasUniqueValidation) {
        dispatch(Ax.updateRub(rubId, {showGradLblValidations: true}));
      } else {
        dispatch(Ax.updateRub(rubId, {editGradLbls, showGradLblValidations: false}));
        dispatch(Ax.sortGradLbls(rubId));
      }
    }
  },

};



/*
 *  Reducer
 */

const initialState = {
  rubMap: {},
  critMap: {},
  optMap: {},
  gradLblMap: {},
};

const reducer = reducerUtils.createReducer(initialState, {

  [Types.SET_RUB]: (state, {rubric}) => {
    return {...state,
      rubMap: {...state.rubMap,
        [rubric.id]: {...rubric,
          critIds: (rubric.crits || []).map(c => c.id),
          gradLblIds: (rubric.gradLbls || []).map(gl => gl.id),
          crits: undefined,
          hasTouchedWeights: false,
        },
      },
    };
  },
  [Types.SET_CRIT]: (state, {crit, rubId}) => {
    return {...state,
      critMap: {...state.critMap,
        [crit.id]: {...crit,
          rubId,
          optIds: crit.opts ? (crit.opts || []).map(o => o.id) : null,
          range: crit.range ? {...crit.range} : null,
          opts: undefined,
          presetKey: helpers.findPresetKey(crit),
        },
      },
    };
  },
  [Types.SET_OPT]: (state, {opt, critId}) => {
    return {...state,
      optMap: {...state.optMap,
        [opt.id]: {...opt, critId},
      },
    };
  },
  [Types.SET_GRAD_LBL]: (state, {gradLbl, rubId}) => {
    return {...state,
      gradLblMap: {...state.gradLblMap,
        [gradLbl.id]: {...gradLbl, rubId},
      },
    };
  },

  [Types.UPDATE_RUB]: (state, {rubId, attrs}) => {
    const prevAttrs = state.rubMap[rubId] || {};
    return {...state,
      rubMap: {...state.rubMap,
        [rubId]: {...prevAttrs, ...attrs},
      },
    };
  },
  [Types.UPDATE_CRIT]: (state, {critId, attrs}) => {
    const prevAttrs = state.critMap[critId] || {};
    const newAttrs = {...prevAttrs, ...attrs};
    if (attrs.range && !attrs.presetKey) newAttrs.presetKey = ScalePresetTypes.RNG_CUSTOM;
    return {...state,
      critMap: {...state.critMap,
        [critId]: newAttrs,
      },
    };
  },
  [Types.UPDATE_OPT]: (state, {optId, attrs}) => {
    const prevAttrs = state.optMap[optId] || {};
    const newState = {...state,
      optMap: {...state.optMap,
        [optId]: {...prevAttrs, ...attrs},
      },
    };
    const crit = state.critMap[prevAttrs.critId];
    if (crit && crit.presetKey !== ScalePresetTypes.OPTS_CUSTOM) {
      newState.critMap = {...state.critMap,
        [crit.id]: {...crit,
          presetKey: ScalePresetTypes.OPTS_CUSTOM,
        },
      };
    }
    return newState;
  },
  [Types.UPDATE_GRAD_LBL]: (state, {gradLblId, attrs}) => {
    const prevAttrs = state.gradLblMap[gradLblId] || {};
    const newState = {...state,
      gradLblMap: {...state.gradLblMap,
        [gradLblId]: {...prevAttrs, ...attrs},
      },
    };
    const rub = state.rubMap[prevAttrs.rubId];
    if (rub && rub.gradLabelPresetKey !== GradLabelPresetTypes.CUSTOM) {
      newState.rubMap = {...state.rubMap,
        [rub.id]: {...rub,
          gradLabelPresetKey: GradLabelPresetTypes.CUSTOM,
        },
      };
    }
    return newState;
  },


  [Types.UNLOAD_OBJS]: (state, {rubIds, critIds, optIds, gradLblIds}) => {
    const rubMap      = rubIds.length     ? {...state.rubMap}      : state.rubMap;
    const critMap     = critIds.length    ? {...state.critMap}     : state.critMap;
    const optMap      = optIds.length     ? {...state.optMap}      : state.optMap;
    const gradLblMap  = gradLblIds.length ? {...state.gradLblMap}  : state.gradLblMap;
    rubIds.forEach(    id => { delete     rubMap[id]; })
    critIds.forEach(   id => { delete    critMap[id]; })
    optIds.forEach(    id => { delete     optMap[id]; })
    gradLblIds.forEach(id => { delete gradLblMap[id]; })
    return {...state, rubMap, critMap, optMap, gradLblMap};
  },

});



/*
 *  Selectors
 */

const Slx = (() => {

  const selRubMap     = state => state.companyAdmin.rubricBuilder.rubMap;
  const selCritMap    = state => state.companyAdmin.rubricBuilder.critMap;
  const selOptMap     = state => state.companyAdmin.rubricBuilder.optMap;
  const selGradLblMap = state => state.companyAdmin.rubricBuilder.gradLblMap;

  const selId = (state, id) => id;

  const buildGradLbl = (id, gradLblMap) => {
    const gradLbl = gradLblMap[id];
    if (!gradLbl) return null;
    return {...gradLbl,
      validations: helpers.getVldGradLbl(gradLbl),
    };
  };

  const buildOpt = (id, optMap) => {
    const opt = optMap[id];
    if (!opt) return null;
    return {...opt,
      validations: helpers.getVldOpt(opt),
    };    
  };

  const buildCrit = (id, critMap, optMap) => {
    const crit = critMap[id];
    if (!crit) return null;

    const opts = crit.optIds ? (crit.optIds || []).map((optId) => {
      return buildOpt(optId, optMap);
    }).filter(o => o) : null;
    const obj = {...crit, opts};
    obj.validations = helpers.getVldCrit(obj);
    return obj;
  };

  const buildRub = (id, rubMap, critMap, optMap, gradLblMap) => {
    const rub = rubMap[id];
    if (!rub) return null;
    const crits = rub.critIds.map((critId) => {
      return buildCrit(critId, critMap, optMap) || null;
    }).filter(c => c);
    const gradLbls = rub.gradLblIds ? rub.gradLblIds.map((gradLblId) => {
      return buildGradLbl(gradLblId, gradLblMap) || null;
    }).filter(gl => gl) : null;
    const obj = {...rub, crits, gradLbls};
    obj.validations = helpers.getVldRub(obj);
    return obj;
  };

  const selRub = createSelector(
    [selId, selRubMap, selCritMap, selOptMap, selGradLblMap],
    (id, rubMap, critMap, optMap, gradLblMap) => buildRub(id, rubMap, critMap, optMap, gradLblMap)
  );

  const selCrit = createSelector(
    [selId, selCritMap, selOptMap],
    (id, critMap, optMap) => buildCrit(id, critMap, optMap)
  );

  const selOpt = createSelector(
    [selId, selOptMap],
    (id, optMap) => buildOpt(id, optMap)
  );

  const selGradLbl = createSelector(
    [selId, selGradLblMap],
    (id, gradLblMap) => buildGradLbl(id, gradLblMap)
  );

  const selRubLowestGradLblVal = createSelector(
    [selId, selRub],
    (id, rub) => {
      if (!rub?.gradLbls) return null;
      let lowest = 100_000;
      rub.gradLbls.forEach((gradLbl) => {
        if (gradLbl.threshold && (gradLbl.threshold < lowest)) lowest = gradLbl.threshold;
      });
      return lowest;
    }
  );

  const selSaveAttrs = createSelector(
    [selId, selRub],
    (rubId, rub) => {
      if (!rub) return null;
      return {
        id: rub.id,
        normType: rub.normType || NormTypes.PERCENTAGE,
        crits: (rub.crits || []).map((crit) => {
          const cObj = _.pick(crit, ['id', 'weight']);
          cObj.label = (crit.label || '').trim();
          const hasOpts = !!crit.opts?.length;
          const hasRange = !hasOpts && !!crit.range;
          cObj.opts = (() => {
            if (!hasOpts) return null;
            return _.orderBy(crit.opts.map((opt) => {
              const oObj = _.pick(opt, ['id', 'value', 'style']);
              oObj.label = (opt.label || '').trim();
              return oObj;
            }), 'value', 'asc');
          })();
          cObj.range = hasRange ? _.pick(crit.range, ['min', 'max']) : null;
          return cObj;
        }),
        gradLbls: (() => {
          if (rub.normType !== NormTypes.GRAD_LABELS) return null;
          const objs = (rub.gradLbls || []).map((gradLbl) => {
            const glObj = _.pick(gradLbl, ['id', 'style', 'threshold']);
            glObj.label = (gradLbl.label || '').trim();
            return glObj;
          });
          return _.orderBy(objs, 'threshold', 'desc');
        })(),
      };
    }
  );

  const selIsValid = createSelector(
    [selId, selRub],
    (rubId, rub) => {
      return !rub?.validations;
    }
  );

  return {
    rubMap: selRubMap,
    critMap: selCritMap,
    optMap: selOptMap,
    gradLblMap: selGradLblMap,
    rub: selRub,
    crit: selCrit,
    opt: selOpt,
    gradLbl: selGradLbl,
    saveAttrs: selSaveAttrs,
    isValid: selIsValid,
    rubLowestGradLblVal: selRubLowestGradLblVal,
  };

})();



export {Types, Ax, reducer, Slx};
export default {Types, Ax, reducer, Slx};
